import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { FileViewer } from '@capacitor/file-viewer';
import { Share } from '@capacitor/share';
import { getMimeTypeFromFilename, MimeType } from '@/utils/fileValidation';

type PluginCommand =
  | 'writeFile'
  | 'readFile'
  | 'checkPermissions'
  | 'requestPermissions'
  | 'openBrowser'
  | 'openUrl'
  | 'exitApp'
  | 'openFile'
  | 'share';

interface BridgeRequest {
  type: 'CAPACITOR_COMMAND';
  command: PluginCommand;
  params?: any;
  messageId: string;
}

interface BridgeResponse {
  messageId: string;
  result?: any;
  error?: {
    message: string;
    stack?: string;
    command?: string;
    params?: any;
  };
}

const TRUSTED_ORIGIN =
  window.location.origin && window.location.origin !== 'null'
    ? window.location.origin
    : '*';

export class CapacitorBridge {
  static Directory = Directory;
  static Encoding = Encoding;

  private static isTop = window.self === window.top;

  static init() {
    if (!this.isTop) return;

    window.addEventListener('message', async (event: MessageEvent) => {
      console.log(
        'CapacitorBridge received message:',
        event.data,
        'from',
        event.origin,
      );
      const data = event.data as BridgeRequest;
      if (!data || data.type !== 'CAPACITOR_COMMAND') return;
      if (event.origin !== TRUSTED_ORIGIN) return;

      const { command, params, messageId } = data;
      const source = event.source as Window;

      try {
        const handler = this.pluginMethods[command];
        if (!handler) throw new Error(`Unknown Capacitor command: ${command}`);

        const result = await handler(params);
        source.postMessage(
          { messageId, result } as BridgeResponse,
          TRUSTED_ORIGIN,
        );
      } catch (err: any) {
        const response: BridgeResponse = {
          messageId,
          error: {
            message: err?.message || 'Unknown native error',
            stack: err?.stack,
            command,
            params,
          },
        };
        source.postMessage(response, TRUSTED_ORIGIN);
      }
    });
  }

  static execute<T = any>(command: PluginCommand, params?: any): Promise<T> {
    if (this.isTop) return this.pluginMethods[command](params);
    return this.executeInTop<T>(command, params);
  }

  private static executeInTop<T>(
    command: PluginCommand,
    params?: any,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const messageId = crypto.randomUUID();

      const listener = (event: MessageEvent) => {
        const data = event.data as BridgeResponse;
        if (!data || data.messageId !== messageId) return;
        if (event.origin !== TRUSTED_ORIGIN) return;

        window.removeEventListener('message', listener);

        if (data.error) reject(new Error(data.error.message));
        else resolve(data.result);
      };

      window.addEventListener('message', listener);

      const request: BridgeRequest = {
        type: 'CAPACITOR_COMMAND',
        command,
        params,
        messageId,
      };
      window.top!.postMessage(request, TRUSTED_ORIGIN);

      setTimeout(() => {
        window.removeEventListener('message', listener);
        reject(new Error(`Timeout waiting for command: ${command}`));
      }, 15000);
    });
  }

  private static pluginMethods: Record<
    PluginCommand,
    (params?: any) => Promise<any>
  > = {
    async writeFile(params) {
      const filename = params?.path?.split('/').pop() || `export-${Date.now()}`;
      const isBinary = params?.isBinary ?? true;
      const encoding = isBinary ? undefined : Encoding.UTF8;

      try {
        const result = await Filesystem.writeFile({
          path: filename,
          data: params.data,
          directory: Directory.Documents,
          encoding,
        });

        return { uri: result.uri };
      } catch (err) {
        console.warn('Documents write failed, falling back to Cache', err);
      }

      const cacheResult = await Filesystem.writeFile({
        path: filename,
        data: params.data,
        directory: Directory.Cache,
        encoding,
      });

      return { uri: cacheResult.uri };
    },

    async openFile(params) {
      const path = params?.filePath;
      if (!path) throw new Error('Invalid filePath for openFile');

      return FileViewer.openDocumentFromLocalPath({ path });
    },

    readFile(params) {
      return Filesystem.readFile(params);
    },

    checkPermissions() {
      return Filesystem.checkPermissions();
    },

    requestPermissions() {
      return Filesystem.requestPermissions();
    },

    openBrowser(params) {
      return Browser.open({
        windowName: '_blank',
        toolbarColor: '#ffffff',
        ...params,
      });
    },

    openUrl(params) {
      const url = params?.url;
      if (!url) throw new Error('Missing URL');
      return Browser.open({ url });
    },

    share(params) {
      const { title, text, url } = params || {};
      return Share.share({ title, text, url });
    },

    exitApp() {
      return App.exitApp();
    },
  };

  static getMimeTypeForPath(path: string): MimeType | undefined {
    if (!path) return undefined;
    const mimeType = getMimeTypeFromFilename(path);
    return mimeType === MimeType.OctetStream ? undefined : mimeType;
  }
}
